import { backendApi } from "@/services/backend/api";
import { Address } from "@/services/backend/addresses/types/address";
import { CompanyProfile } from "@/services/backend/company/types/company-profile";

const companyProfileApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    showCompanyProfile: build.query<CompanyProfile, ShowCompanyProfileRequest>({
      query: (request) => ({
        url: `/companies/company/profile`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "CompanyProfile", id: result.companyId }] : [],
    }),
    patchCompanyProfile: build.mutation<
      CompanyProfile,
      PatchCompanyProfileRequest
    >({
      query: (request) => ({
        url: `/companies/company/profile`,
        method: "PATCH",
        body: request,
      }),
      // Only invalidate on error, to reset the optimistically updated cache
      // to the server state. Invalidating on success would refetch and
      // overwrite characters typed while the save was in flight.
      invalidatesTags: (_result, error, request) =>
        error ? [{ type: "CompanyProfile", id: request.companyId }] : [],
    }),
  }),
});

export const { useShowCompanyProfileQuery, usePatchCompanyProfileMutation } =
  companyProfileApi;

/**
 * updateCompanyProfileCache optimistically patches the cached profile.
 *
 * The show query is keyed by companyId rather than by id, so this is
 * spelled out instead of using the `updateCache` helper, which assumes
 * an entity carrying its own `id`.
 */
export const updateCompanyProfileCache = (profile: CompanyProfile) =>
  companyProfileApi.util.updateQueryData(
    "showCompanyProfile",
    { companyId: profile.companyId },
    () => profile,
  );

export interface ShowCompanyProfileRequest {
  companyId: string;
}

export interface PatchCompanyProfileRequest {
  companyId: string;
  address: Address;
}
